import { type DependencyList, type RefObject, useEffect } from 'react';
import { useSelector } from 'react-redux';

import store from '../../store/store';
import { selectIsMobile } from '../../store/selectors/app';
import { articleBasedOnType, chunkArray } from '../../apis/ilink';
import { checkAvailabilityForArticles, getArticles } from '../../apis/masterdata/masterdata';
import { type Article, type AvailableArticle, type MasterDataArticle } from '../../apis/masterdata/masterdata.type';
import { type InterventionType } from '../../apis/interventions/intervention.type';
import { availableArticleLifeStages, emptyArticle, findArticleId, LifeStage } from '../../business/articleHelper';
import { buildServiceArticles, buildSparePartArticles } from '../../business/entityHelper';
import { type StoreIdAndTaxesCondition } from '../../store/selectors/price';

const displayAssociatedEntities = (associatedEntities: HTMLDivElement, arrow: HTMLDivElement) => {
  const parentElement = associatedEntities.parentElement;

  if (!parentElement) {
    return;
  }

  const { bottom: parentBottom } = parentElement.getBoundingClientRect();

  associatedEntities.style.left = '';
  associatedEntities.style.top = '';
  associatedEntities.style.width = `${globalThis.innerWidth - 260}px`;

  const availableSpaceBelow = globalThis.innerHeight - parentBottom - 85;
  const requiredSpace = associatedEntities.clientHeight + 20;
  const isTop = availableSpaceBelow < requiredSpace;

  arrow.classList.toggle('top', isTop);

  const translateX = associatedEntities.getBoundingClientRect().left - 160;
  const translateY = isTop ? associatedEntities.clientHeight + 20 : associatedEntities.parentElement.clientHeight + 20;

  associatedEntities.style.left = `-${translateX}px`;
  associatedEntities.style.top = `${isTop ? '-' : ''}${translateY}px`;
  associatedEntities.style.visibility = 'visible';
  arrow.style.visibility = 'visible';
};

type DisplayAssociatedServicesProps = {
  overlayElement: RefObject<HTMLDivElement | null>;
  arrowElement: RefObject<HTMLDivElement | null>;
  dependencies: DependencyList;
};

export const useDisplayAssociatedServices = ({ overlayElement, arrowElement, dependencies }: DisplayAssociatedServicesProps) => {
  const isMobile = useSelector(selectIsMobile);

  useEffect(() => {
    if (!isMobile && overlayElement.current && arrowElement.current) {
      displayAssociatedEntities(overlayElement.current, arrowElement.current);
    }
  }, dependencies);
};

type ArticlesCallBack = (articles: Article[], masterdataArticles: MasterDataArticle[]) => Article[];

const buildArticleFromInformation = (articleId: number, availabilities: AvailableArticle[]): Article => {
  const availability = availabilities.find(findArticleId(articleId));

  return {
    articleId,
    pogoId: null,
    ...availability,
  };
};

const getArticlesInformation = ({
  modelCodes,
  lifestages,
  callback,
  priceOptions,
}: {
  modelCodes: number[];
  lifestages: LifeStage[];
  callback: ArticlesCallBack;
  priceOptions: StoreIdAndTaxesCondition;
}): Promise<Article[]> =>
  getArticles(modelCodes).then(masterdataArticles => {
    const chunkArticles = chunkArray([...new Set(masterdataArticles.map(article => article.articleId))], 20);

    return Promise.all(
      chunkArticles.map(async articleIds => ({
        articleIds,
        availabilities: await checkAvailabilityForArticles(articleIds, lifestages, priceOptions.currentStoreId),
      }))
    )
      .then(articleInformations =>
        articleInformations.flatMap(({ articleIds, availabilities }) =>
          articleIds.map(articleId => buildArticleFromInformation(articleId, availabilities))
        )
      )
      .then(articles => callback(articles, masterdataArticles));
  });

export const fillSparePartsArticles = (modelCodes: number[], priceOptions: StoreIdAndTaxesCondition): Promise<Article[]> =>
  getArticlesInformation({
    modelCodes,
    lifestages: availableArticleLifeStages,
    callback: (articles: Article[], masterdataArticles: MasterDataArticle[]) =>
      modelCodes.flatMap(modelCode =>
        buildSparePartArticles(
          masterdataArticles
            .filter(article => article.modelCode === modelCode)
            .map(article => ({
              ...emptyArticle(),
              ...article,
              ...articles.find(findArticleId(article.articleId)),
              ...articleBasedOnType(),
            }))
        )
      ),
    priceOptions,
  }).then(articles => [...new Map(articles.map(article => [article.articleId, article])).values()]);

export const getServicesArticles = (
  modelCodes: number[],
  priceOptions: StoreIdAndTaxesCondition,
  interventionType?: InterventionType
): Promise<Article[]> =>
  getArticlesInformation({
    modelCodes,
    lifestages: [LifeStage.active],
    callback: (articles: Article[], masterdataArticles: MasterDataArticle[]) =>
      modelCodes.flatMap(modelCode =>
        buildServiceArticles(
          masterdataArticles
            .filter(
              article => article.modelCode === modelCode && articles.some(articleInformation => articleInformation.articleId === article.articleId)
            )
            .map(article => {
              return {
                ...emptyArticle(),
                ...article,
                ...articles.find(findArticleId(article.articleId)),
              };
            }),
          interventionType ?? store.getState().intervention.interventionType
        )
      ),
    priceOptions,
  });
